import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../lib/utils";

/* The Sirdab alert: an inline notice that REPORTS, never commands. Taken
   from shadcn (missing component → shadcn, per the house rule) and trued:
   the stock bordered box with an icon gutter becomes the subtle banner
   pair TOKENS.md prescribes ("a success or warning banner: --*-subtle +
   --foreground"), the body type role replaces the stock small-text utility, and
   there is no icon slot: a banner is read, not scanned.

   Variants (pick by what the notice REPORTS):
     default      context the reader should have: the tonal muted band.
     warning      something to weigh before acting: warning-subtle.
     success      a confirmation that stays on the page: success-subtle.
     destructive  a failure the reader must deal with: destructive-subtle
                  with destructive text, the one pair whose text carries
                  the hue (errors are red text). Announced as role=alert;
                  every other variant is a polite status.

   Success and warning keep FOREGROUND text: the tint reports and the text
   stays legible. Nothing here is a fill a button may wear. Callers pass
   every string; the primitive stays i18n-free.

   Usage:
       <Alert variant="warning">
         <AlertTitle>{t("fee.disclosure_title")}</AlertTitle>
         <AlertDescription>{t("fee.deducted_note")}</AlertDescription>
       </Alert> */
const alertVariants = cva(
  "text-body flex w-full flex-col gap-1 rounded-lg px-4 py-3",
  {
    variants: {
      variant: {
        default: "bg-muted text-foreground",
        warning: "bg-warning-subtle text-foreground",
        success: "bg-success-subtle text-foreground",
        destructive: "bg-destructive-subtle text-destructive",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function Alert({
  className,
  variant = "default",
  role,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      data-variant={variant}
      role={role ?? (variant === "destructive" ? "alert" : "status")}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="alert-title"
      className={cn("text-label", className)}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn("text-body flex flex-col gap-1", className)}
      {...props}
    />
  );
}

export { Alert, AlertDescription, AlertTitle, alertVariants };
